/**
 * Browser geolocation capture.
 *
 * Returns a structured result rather than throwing, because every failure mode
 * here is expected in normal use — denied permission, no fix indoors, a slow
 * GPS lock. The caller needs to distinguish them to say something useful, and
 * a check-in must still be recordable when the location is unavailable.
 */

export interface CapturedLocation {
  latitude: number;
  longitude: number;
  accuracyM: number;
}

export type GeoFailure =
  | 'unsupported'
  | 'permission_denied'
  | 'unavailable'
  | 'timeout';

export type GeoResult =
  | { ok: true; location: CapturedLocation }
  | { ok: false; reason: GeoFailure; message: string };

const MESSAGES: Record<GeoFailure, string> = {
  unsupported: 'This browser cannot report location.',
  permission_denied:
    'Location permission is blocked. Enable it in your browser settings to check in from site.',
  unavailable: 'Location is unavailable right now — try moving near a window.',
  timeout: 'Getting a location fix took too long.',
};

export function captureLocation(timeoutMs = 10_000): Promise<GeoResult> {
  if (!('geolocation' in navigator)) {
    return Promise.resolve({
      ok: false,
      reason: 'unsupported',
      message: MESSAGES.unsupported,
    });
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          ok: true,
          location: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyM: position.coords.accuracy,
          },
        }),
      (error) => {
        const reason: GeoFailure =
          error.code === error.PERMISSION_DENIED
            ? 'permission_denied'
            : error.code === error.TIMEOUT
              ? 'timeout'
              : 'unavailable';
        resolve({ ok: false, reason, message: MESSAGES[reason] });
      },
      {
        enableHighAccuracy: true,
        timeout: timeoutMs,
        // Never reuse a cached fix: a stale position would let someone check in
        // from where they were an hour ago.
        maximumAge: 0,
      },
    );
  });
}

/** Whether location permission is already granted, without prompting. */
export async function permissionState(): Promise<PermissionState | 'unknown'> {
  if (!('permissions' in navigator)) return 'unknown';
  try {
    const status = await navigator.permissions.query({
      name: 'geolocation' as PermissionName,
    });
    return status.state;
  } catch {
    return 'unknown';
  }
}
